import * as React from 'react';
import * as _ from 'lodash';
import * as classnames from 'classnames';
import {
    Panel,
    Project,
    PROJECT_STATUS,
    ProjectLanguage,
    QUOTA_STATUS,
    RedirectUrlSettings
} from '@nexxt/common/types';
import Toggle from '../../../../common/Toggle';
import { UilTrashAlt } from '@iconscout/react-unicons';

interface PanelSourceProps {
    index: number;
    panel: Panel;
    projectId: number;
    errorObject: any;
    currentProject: Project;
    disabled: boolean;
    updatePanelSettingsByKeyName: (keyName: string, value: any) => void;
}

const PanelSource = ({
    index,
    panel,
    projectId,
    errorObject,
    currentProject,
    disabled,
    updatePanelSettingsByKeyName
}: PanelSourceProps): JSX.Element => {
    const { advancedSettings } = panel;
    const isProjectLaunched = currentProject.status !== PROJECT_STATUS.DRAFT;
    const codename = isProjectLaunched
        ? currentProject.codeName
        : `${projectId}_PREVIEW`;
    const testModeParam = isProjectLaunched ? '' : '?testMode=true';
    const isMultiLingualProject = currentProject.languages.length > 1;
    const showSettings = isMultiLingualProject;
    let botUrl = `${process.env.INCA_CHATBOT_DOMAIN}/p/${codename}${testModeParam}`;
    if (index) {
        const qmarkOrAmpersand = botUrl.includes('?') ? '&' : '?';
        botUrl = botUrl + qmarkOrAmpersand + `src=${panel.key || 'PANEL_NAME'}`;
    }

    if (panel.identifierKeys.length) {
        const qmarkOrAmpersand = botUrl.includes('?') ? '&' : '?';
        let urlParams = '';

        panel.identifierKeys.forEach((param) => {
            urlParams =
                urlParams +
                qmarkOrAmpersand +
                `${param.identifierParamKey}={unique_id}`;
        });

        botUrl = botUrl + urlParams;
    }

    // ?${
    //     panel.identifierParamKey || '{{parameter_key}}'
    // }={{unique_respondent_id}}${testModeParam}`;

    // // skip SRC for the general panel
    // if (index) {
    //     botUrl += `&src=${panel.key || 'PANEL_NAME'}`;
    // }

    const updatePanelRedirectByStatus = (
        status: QUOTA_STATUS,
        keyName: string,
        value: any
    ) => {
        const redirectUrlSettings = {
            ...panel.redirectUrlSettings,
            [status]: {
                ...panel.redirectUrlSettings[status],
                [keyName]: value
            }
        };
        updatePanelSettingsByKeyName(
            'redirectUrlSettings',
            redirectUrlSettings
        );
    };

    const addNewPanelIdentifier = () => {
        const { identifierKeys } = panel;
        identifierKeys.push({
            identifierParamKey: '',
            redirectIdentifierParamKey: ''
        });
        updatePanelSettingsByKeyName('identifierKeys', identifierKeys);
    };

    const updatePanelIdentifier = (
        index: number,
        keyName: string,
        value: string
    ) => {
        const { identifierKeys } = panel;

        updatePanelSettingsByKeyName('identifierKeys', [
            ...identifierKeys.slice(0, index),
            { ...identifierKeys[index], [keyName]: value },
            ...identifierKeys.slice(index + 1)
        ]);
    };

    const deletePanelIdentifier = (index: number) => {
        const { identifierKeys } = panel;

        updatePanelSettingsByKeyName('identifierKeys', [
            ...identifierKeys.slice(0, index),
            ...identifierKeys.slice(index + 1)
        ]);
    };

    return (
        <div className="panel-source">
            <div className="panel-source--row">
                <div className="panel-source--column">
                    <div
                        className={classnames('panel-source--item', {
                            error: errorObject?.name
                        })}
                    >
                        <div
                            className={classnames('panel-source--item--inner', {
                                error: errorObject?.name
                            })}
                        >
                            <span>Name/Label</span>{' '}
                            <input
                                type="text"
                                value={panel.name}
                                disabled={!index || disabled}
                                placeholder="Panel name"
                                onChange={(e) =>
                                    updatePanelSettingsByKeyName(
                                        'name',
                                        e.target.value
                                    )
                                }
                            />
                        </div>
                        {errorObject?.name ? (
                            <div className="panel-source--item--inner justify-end error-message">
                                {errorObject?.name}
                            </div>
                        ) : null}
                    </div>
                    <div className="panel-source--item"></div>
                </div>
                <div className="panel-source--column"></div>
            </div>
            {panel?.identifierKeys?.map((identifier, idIndex) => {
                return (
                    <div
                        className="panel-source--row"
                        key={`${panel.name}-${idIndex}`}
                    >
                        <div className="panel-source--column">
                            <div
                                className={classnames('panel-source--item', {
                                    error: errorObject?.identifierKeys?.[
                                        idIndex
                                    ]?.identifierParamKey
                                })}
                            >
                                <div
                                    className={classnames(
                                        'panel-source--item--inner',
                                        {
                                            error: errorObject
                                                ?.identifierKeys?.[idIndex]
                                                ?.identifierParamKey
                                        }
                                    )}
                                >
                                    <span>
                                        Identifier Parameter{' '}
                                        {idIndex ? idIndex + 1 : ''}
                                        {idIndex ? (
                                            <span
                                                className="panel-source--item--delete"
                                                onClick={() =>
                                                    deletePanelIdentifier(
                                                        idIndex
                                                    )
                                                }
                                            >
                                                <UilTrashAlt size={15} />
                                            </span>
                                        ) : null}
                                    </span>{' '}
                                    <input
                                        type="text"
                                        value={identifier.identifierParamKey}
                                        placeholder="i.e. id, psid, etc..."
                                        onChange={(e) =>
                                            updatePanelIdentifier(
                                                idIndex,
                                                'identifierParamKey',
                                                e.target.value
                                            )
                                        }
                                        disabled={!index || disabled}
                                    />
                                </div>
                                {errorObject?.identifierKeys?.[idIndex]
                                    ?.identifierParamKey ? (
                                    <div className="panel-source--item--inner justify-end error-message">
                                        {
                                            errorObject?.identifierKeys?.[
                                                idIndex
                                            ]?.identifierParamKey
                                        }
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className="panel-source--column">
                            {advancedSettings ? (
                                <div className="panel-source--item">
                                    <div className="panel-source--item--inner">
                                        <span>Redirect Identifier</span>{' '}
                                        <input
                                            type="text"
                                            value={
                                                identifier.redirectIdentifierParamKey
                                            }
                                            onChange={(e) =>
                                                updatePanelIdentifier(
                                                    idIndex,
                                                    'redirectIdentifierParamKey',
                                                    e.target.value
                                                )
                                            }
                                            disabled={disabled}
                                        />
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {/* <div onClick={() => deletePanelIdentifier(idIndex)}>
                                Delete
                            </div> */}
                    </div>
                );
            })}
            <div className="panel-source--row flex-end">
                <button
                    className="btn btn--sm btn--blue"
                    onClick={addNewPanelIdentifier}
                    disabled={disabled || !index}
                >
                    Add more identifier parameters
                </button>
            </div>
            <hr />
            <div className="panel-source--row">
                <div className="panel-source--column">
                    <div className="panel-source--item">
                        <div className="panel-source--item--inner">
                            <span>Enable Forced Redirects</span>{' '}
                            <Toggle
                                checked={panel.enableRedirect}
                                onChange={() =>
                                    updatePanelSettingsByKeyName(
                                        'enableRedirect',
                                        !panel.enableRedirect
                                    )
                                }
                                className="without-label"
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </div>
                <div className="panel-source--column"></div>
            </div>

            <div className="panel-source--row">
                <PanelSourceRedirect
                    panel={panel}
                    redirectUrlSettings={panel.redirectUrlSettings}
                    type={QUOTA_STATUS.COMPLETE}
                    advancedSettings={advancedSettings}
                    disabled={!panel.enableRedirect || disabled}
                    errorObject={errorObject}
                    languages={currentProject.languages}
                    updatePanelRedirectByStatus={updatePanelRedirectByStatus}
                />
                <PanelSourceRedirect
                    panel={panel}
                    redirectUrlSettings={panel.redirectUrlSettings}
                    type={QUOTA_STATUS.TERMINATE}
                    advancedSettings={advancedSettings}
                    disabled={!panel.enableRedirect || disabled}
                    errorObject={errorObject}
                    languages={currentProject.languages}
                    updatePanelRedirectByStatus={updatePanelRedirectByStatus}
                />
            </div>
            <div className="panel-source--row">
                <PanelSourceRedirect
                    panel={panel}
                    redirectUrlSettings={panel.redirectUrlSettings}
                    type={QUOTA_STATUS.QUOTA_FULL}
                    advancedSettings={advancedSettings}
                    disabled={!panel.enableRedirect || disabled}
                    errorObject={errorObject}
                    languages={currentProject.languages}
                    updatePanelRedirectByStatus={updatePanelRedirectByStatus}
                />
                <PanelSourceRedirect
                    panel={panel}
                    redirectUrlSettings={panel.redirectUrlSettings}
                    type={QUOTA_STATUS.SECURITY}
                    advancedSettings={advancedSettings}
                    disabled={!panel.enableRedirect || disabled}
                    errorObject={errorObject}
                    languages={currentProject.languages}
                    updatePanelRedirectByStatus={updatePanelRedirectByStatus}
                />
            </div>
            {showSettings ? (
                <>
                    <hr />
                    <div className="panel-source--row">
                        <div className="panel-source--column">
                            <div className="panel-source--item">
                                <div className="panel-source--item--inner">
                                    <span>Settings</span>
                                </div>
                            </div>
                        </div>
                        <div className="panel-source--column"></div>
                    </div>
                    {isMultiLingualProject ? (
                        <>
                            <div className="panel-source--row">
                                <div className="panel-source--column">
                                    <div className="panel-source--item">
                                        <div className="panel-source--item--inner">
                                            <span>
                                                Mapping Language code to
                                                redirects
                                            </span>
                                            <Toggle
                                                checked={
                                                    panel.mappingLanguageCode
                                                }
                                                onChange={(checked) =>
                                                    updatePanelSettingsByKeyName(
                                                        'mappingLanguageCode',
                                                        checked
                                                    )
                                                }
                                                className="without-label"
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="panel-source--column"></div>
                            </div>
                            {panel.mappingLanguageCode
                                ? currentProject.languages?.map((l) => {
                                      const languageMappingValues = panel
                                          .languageMappingValues?.[l.value] || {
                                          parameter: '',
                                          value: ''
                                      };
                                      return (
                                          <div
                                              className="panel-source--row"
                                              key={`panel-language-${l.value}`}
                                          >
                                              <div className="panel-source--column">
                                                  <div className="panel-source--item">
                                                      <div className="panel-source--item--inner">
                                                          <span>{l.label}</span>
                                                          <input
                                                              type="text"
                                                              placeholder="Language parameter key(i.e. lang)"
                                                              value={
                                                                  languageMappingValues?.parameter
                                                              }
                                                              onChange={(e) =>
                                                                  updatePanelSettingsByKeyName(
                                                                      'languageMappingValues',
                                                                      {
                                                                          ...panel.languageMappingValues,
                                                                          [l.value]:
                                                                              {
                                                                                  ...languageMappingValues,
                                                                                  parameter:
                                                                                      e
                                                                                          .target
                                                                                          .value
                                                                              }
                                                                      }
                                                                  )
                                                              }
                                                              disabled={
                                                                  disabled
                                                              }
                                                          />
                                                      </div>
                                                  </div>
                                              </div>
                                              <div className="panel-source--column">
                                                  <div className="panel-source--item">
                                                      <div className="panel-source--item--inner">
                                                          <input
                                                              type="text"
                                                              placeholder={`Language parameter value(i.e. ${l.value}`}
                                                              value={
                                                                  languageMappingValues?.value
                                                              }
                                                              onChange={(e) =>
                                                                  updatePanelSettingsByKeyName(
                                                                      'languageMappingValues',
                                                                      {
                                                                          ...panel.languageMappingValues,
                                                                          [l.value]:
                                                                              {
                                                                                  ...languageMappingValues,
                                                                                  value: e
                                                                                      .target
                                                                                      .value
                                                                              }
                                                                      }
                                                                  )
                                                              }
                                                              disabled={
                                                                  disabled
                                                              }
                                                          />
                                                      </div>
                                                  </div>
                                              </div>
                                          </div>
                                      );
                                  })
                                : null}
                        </>
                    ) : null}{' '}
                </>
            ) : null}
            <div className="panel-source--row">
                <div className="panel-source--column">
                    <div className="panel-source--item">
                        <div className="panel-source--item--inner">
                            <span>
                                {isProjectLaunched ? 'Live' : 'Preview'} Link
                            </span>{' '}
                            <span className="panel-source--link">
                                {' '}
                                {botUrl}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FRIENDLY_QUOTA_STATUS = {
    [QUOTA_STATUS.COMPLETE]: 'complete',
    [QUOTA_STATUS.TERMINATE]: 'termination',
    [QUOTA_STATUS.QUOTA_FULL]: 'quota full',
    [QUOTA_STATUS.SECURITY]: 'quality disqualification'
};
interface PanelSourceRedirectProps {
    type: QUOTA_STATUS;
    panel: Panel;
    redirectUrlSettings: { [key in QUOTA_STATUS]?: RedirectUrlSettings };
    advancedSettings: boolean;
    disabled: boolean;
    errorObject: any;
    languages: ProjectLanguage[];
    updatePanelRedirectByStatus: (
        status: QUOTA_STATUS,
        keyName: string,
        value: any
    ) => void;
}

const PanelSourceRedirect = ({
    type,
    panel,
    redirectUrlSettings,
    advancedSettings,
    disabled,
    errorObject,
    languages,
    updatePanelRedirectByStatus
}: PanelSourceRedirectProps) => {
    const redirectUrlSetting = redirectUrlSettings[type];
    let redirectUrl = redirectUrlSetting?.url;
    const getTrackerParameter = (identifierKeys) => {
        return identifierKeys?.map(
            (identifier) =>
                `${
                    identifier?.redirectIdentifierParamKey ||
                    identifier.identifierParamKey
                }={unique_id}`
        );
    };

    if (
        redirectUrl &&
        redirectUrlSetting.includeParameters &&
        panel.identifierKeys.length
    ) {
        const qmarkOrAmpersand = redirectUrl.includes('?') ? '&' : '?';
        const trackerIdExample = getTrackerParameter(panel.identifierKeys).join(
            '&'
        );
        redirectUrl = redirectUrl + qmarkOrAmpersand + trackerIdExample;
    }

    if (redirectUrl && panel.mappingLanguageCode) {
        const qmarkOrAmpersand = redirectUrl.includes('?') ? '&' : '?';
        const firstLanguageMappingValues = panel.languageMappingValues
            ? panel.languageMappingValues[languages[0].value]
            : { parameter: '', value: languages[0].value };
        const languageParameter =
            firstLanguageMappingValues?.parameter || 'lang';
        const languageValue =
            firstLanguageMappingValues?.value || languages[0].value;
        redirectUrl =
            redirectUrl +
            qmarkOrAmpersand +
            `${languageParameter}=${languageValue}`;
    }

    const friendlyStatusName = FRIENDLY_QUOTA_STATUS[type];
    return (
        <div className="panel-source--column">
            <div
                className={classnames('panel-source--item', {
                    error: errorObject?.redirectUrlSettings?.[type]?.url
                })}
            >
                <div
                    className={classnames('panel-source--item--inner', {
                        error: errorObject?.redirectUrlSettings?.[type]?.url
                    })}
                >
                    <span>Redirect on {friendlyStatusName}</span>
                    <input
                        type="text"
                        value={redirectUrlSetting.url}
                        onChange={(e) =>
                            updatePanelRedirectByStatus(
                                type,
                                'url',
                                e.target.value
                            )
                        }
                        placeholder={`i.e. https://nexxt.in?status=${_.snakeCase(
                            friendlyStatusName
                        )}`}
                        disabled={disabled}
                    />
                </div>
                {errorObject?.redirectUrlSettings?.[type]?.url ? (
                    <div className="panel-source--item--inner justify-end error-message">
                        {errorObject?.redirectUrlSettings?.[type]?.url}
                    </div>
                ) : null}
            </div>
            {disabled ? null : (
                <div className="panel-source--item p-0">
                    <div className="panel-source--item--inner justify-end">
                        <span className="panel-source--redirect-link">
                            {redirectUrl}
                        </span>
                    </div>
                </div>
            )}
            {advancedSettings ? (
                <div className="panel-source--item">
                    <div className="panel-source--item--inner  justify-end">
                        <Toggle
                            label="Include parameter in redirect url"
                            className="without-label"
                            checked={redirectUrlSetting.includeParameters}
                            onChange={() =>
                                updatePanelRedirectByStatus(
                                    type,
                                    'includeParameters',
                                    !redirectUrlSetting.includeParameters
                                )
                            }
                            disabled={disabled}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
export default PanelSource;
